import moment from 'moment'
import { MODAL_STEPS, WEEK_DAYS } from './options'

const formatAddress = data => {
  const address = data.reduce((acc, item, i, arr) => {
    const skipTypes = [
      'postal_code',
      'country',
      'administrative_area_level_1',
      'administrative_area_level_2',
      'postal_code_suffix',
    ]

    if (item.types && !item.types.some(type => skipTypes.includes(type))) {
      acc += `${item.short_name}`

      const isOnlyRoute = item.types.length === 1 && item.types[0] === 'route'
      const isNotLast = i < arr.length - 1

      if (isOnlyRoute && isNotLast) {
        acc += ','
      }

      acc += ' '
    }

    return acc
  }, '')

  return address.trim()
}
const generateNextButtonLabel = (currentStep, type) => {
  if (currentStep === MODAL_STEPS.BASE_INFORMATION) {
    return 'Next'
  } else if (type === 'add') {
    return 'Create'
  } else {
    return 'Save'
  }
}

const generateBackButtonLabel = currentStep => {
  if (currentStep === MODAL_STEPS.BASE_INFORMATION) {
    return 'Cancel'
  } else {
    return 'Back'
  }
}

const getDefaultCenter = (city, locationForEdit) => {
  if (locationForEdit) {
    return {
      lat: locationForEdit.latitude,
      lng: locationForEdit.longitude,
    }
  }
  return {
    lat: city.latitude ? city.latitude : 0,
    lng: city.longitude ? city.longitude : 0,
  }
}

const timeToMinutes = timeString => {
  if (!timeString) return null
  const [time, period] = timeString.split(' ')
  const [hours, minutes] = time.split(':').map(Number)

  let totalMinutes = hours * 60 + minutes

  if (period === 'PM' && hours !== 12) {
    totalMinutes += 12 * 60
  } else if (period === 'AM' && hours === 12) {
    totalMinutes = minutes
  }

  return totalMinutes
}

const validate12HourFormat = timeString => {
  if (!timeString) return false

  const timeRegex = /^(0?[1-9]|1[0-2]):[0-5][0-9]\s(AM|PM)$/i

  if (!timeRegex.test(timeString)) {
    return false
  }

  const [time, period] = timeString.split(' ')
  const [hours, minutes] = time.split(':').map(Number)

  if (hours < 1 || hours > 12) return false
  if (minutes < 0 || minutes > 59) return false
  if (!['AM', 'PM'].includes(period.toUpperCase())) return false

  return true
}

const validateBreakTime = (
  id,
  start,
  end,
  validatedField,
  handleValidationError,
  breakTimes,
  errorKey = 'breakTimes',
) => {
  let startError = null
  let endError = null
  let hasErrors = false

  if (validatedField === 'start' && !start) {
    startError = 'Start time is required'
    hasErrors = true
  }

  if (validatedField === 'end' && !end) {
    endError = 'End time is required'
    hasErrors = true
  }

  if (start && end) {
    const startMinutes = timeToMinutes(start)
    const endMinutes = timeToMinutes(end)
    if (
      startMinutes !== null &&
      endMinutes !== null &&
      startMinutes >= endMinutes
    ) {
      hasErrors = true
      if (validatedField === 'start') {
        startError = 'Operational time start must be before end'
      } else {
        endError = 'Operational time end must be after start'
      }
    }

    const timeDifference = endMinutes - startMinutes

    if (timeDifference < 180 && timeDifference >= 0) {
      hasErrors = true
      startError =
        'The duration for an operational time block must be at least 3 hours.'
      endError = ' '
    }
  }

  if (start && !validate12HourFormat(start)) {
    startError = 'Start time must be in 12-hour format (e.g., 07:00 AM)'
    hasErrors = true
  }

  if (end && !validate12HourFormat(end)) {
    endError = 'End time must be in 12-hour format (e.g., 11:00 PM)'
    hasErrors = true
  }

  const haveOverlaps = validateBreakTimeOverlap(
    breakTimes,
    handleValidationError,
    errorKey,
  )

  handleValidationError(`${errorKey}-${id}-start`, startError)
  handleValidationError(`${errorKey}-${id}-end`, endError)

  return hasErrors || haveOverlaps
}

const validateBreakTimeOverlap = (
  breakTimes,
  handleValidationError,
  errorKey = 'breakTimes',
) => {
  const preparedTimes = breakTimes
    .filter(
      breakTime =>
        breakTime.start &&
        breakTime.end &&
        validate12HourFormat(breakTime.start) &&
        validate12HourFormat(breakTime.end),
    )
    .map(breakTime => {
      return {
        id: breakTime.id,
        start: timeToMinutes(breakTime.start),
        end: timeToMinutes(breakTime.end),
      }
    })
  let hasErrors = false
  preparedTimes.forEach(time => {
    const timesToCompare = preparedTimes.filter(t => t.id !== time.id)
    let hasOverlaps = false

    hasOverlaps = timesToCompare.some(comparedTimeRange => {
      return (
        (time.start >= comparedTimeRange.start &&
          time.start <= comparedTimeRange.end) ||
        (time.end >= comparedTimeRange.start &&
          time.end <= comparedTimeRange.end) ||
        (comparedTimeRange.start >= time.start &&
          comparedTimeRange.start <= time.end) ||
        (comparedTimeRange.end >= time.start &&
          comparedTimeRange.end <= time.end)
      )
    })

    if (hasOverlaps) {
      handleValidationError(
        `${errorKey}-${time.id}-start-overlap`,
        'This time-block overlaps with another',
      )
      handleValidationError(`${errorKey}-${time.id}-end-overlap`, ' ')
      hasErrors = true
    } else {
      handleValidationError(`${errorKey}-${time.id}-start-overlap`, null)
      handleValidationError(`${errorKey}-${time.id}-end-overlap`, null)
    }
  })

  return hasErrors
}

const validateBreakTimesNumber = (breakTimes, handleValidationError) => {
  if (breakTimes.length == 0) {
    handleValidationError('breakTimes', 'Operational times are required')
  } else {
    handleValidationError('breakTimes', null)
  }
}

const getDefaultHoursSchedule = () => {
  return WEEK_DAYS.map(day => ({
    day: day.value,
    hours: [{ id: 1, start: null, end: null }],
  }))
}

const getDefaultDaysSchedule = day => {
  return {
    day,
    hours: [{ id: 1, start: null, end: null }],
  }
}

const getDefaultTimeSlotsSchedule = () => {
  return WEEK_DAYS.map(day => ({
    day: day.value,
    slots: { morning: true, lunch: true, afternoon: true },
  }))
}

const getDefaultTimeSlotSchedule = day => {
  return {
    day,
    slots: { morning: true, lunch: true, afternoon: true },
  }
}

const convertDayValueToShortName = day => {
  return WEEK_DAYS.find(dayItem => dayItem.value == day).label
}

const validateYearlyTimeRange = (timeRange, handleValidationError) => {
  let hasErrors = false
  if (!timeRange.start) {
    handleValidationError(
      `yearly-time-range-start-${timeRange.id}`,
      'Is required',
    )
    hasErrors = true
  } else {
    handleValidationError(`yearly-time-range-start-${timeRange.id}`, null)
  }

  if (!timeRange.end) {
    handleValidationError(
      `yearly-time-range-end-${timeRange.id}`,
      'Is required',
    )
    hasErrors = true
  } else {
    handleValidationError(`yearly-time-range-end-${timeRange.id}`, null)
  }

  if (timeRange.start && timeRange.end) {
    const normalizedStart = moment(normalizeYearlyRangeTime(timeRange.start))
    const normalizedEnd = moment(normalizeYearlyRangeTime(timeRange.end))
    if (normalizedStart.isAfter(normalizedEnd)) {
      handleValidationError(
        `yearly-time-range-start-${timeRange.id}`,
        'Start time must be same or before end time',
      )
      handleValidationError(
        `yearly-time-range-end-${timeRange.id}`,
        'End time must be same or after start time',
      )
      hasErrors = true
    } else {
      handleValidationError(`yearly-time-range-start-${timeRange.id}`, null)
      handleValidationError(`yearly-time-range-end-${timeRange.id}`, null)
    }
  }

  return hasErrors
}

const normalizeYearlyRangeTime = time => `2000-${time}`

const validateYearlyRangeStartTime = (
  timeRangeId,
  start,
  end,
  handleValidationError,
) => {
  if (start && end) {
    const normalizedStart = moment(normalizeYearlyRangeTime(start))
    const normalizedEnd = moment(normalizeYearlyRangeTime(end))
    if (normalizedStart.isAfter(normalizedEnd)) {
      handleValidationError(
        `yearly-time-range-start-${timeRangeId}`,
        'Start time must be same or before end time',
      )
      return true
    }
  }
  handleValidationError(`yearly-time-range-start-${timeRangeId}`, null)
  return false
}

const validateYearlyRangeEndTime = (
  timeRangeId,
  start,
  end,
  handleValidationError,
) => {
  if (start && end) {
    const normalizedStart = moment(normalizeYearlyRangeTime(start))
    const normalizedEnd = moment(normalizeYearlyRangeTime(end))
    if (normalizedStart.isAfter(normalizedEnd)) {
      handleValidationError(
        `yearly-time-range-end-${timeRangeId}`,
        'End time must be same or after start time',
      )
      return true
    }
  }
  handleValidationError(`yearly-time-range-end-${timeRangeId}`, null)
  return false
}

const validateYearlyRangeTimesOverlaps = (
  timeRanges,
  handleValidationError,
  editedRangeId,
) => {
  let hasErrors = false

  timeRanges.forEach(range => {
    const rangesToCompare = timeRanges.filter(r => r.id !== range.id)
    const startForValidation = range.start
      ? moment(normalizeYearlyRangeTime(range.start))
      : null
    const endForValidation = range.end
      ? moment(normalizeYearlyRangeTime(range.end))
      : null

    if (!startForValidation || !endForValidation) return

    const overlaping = rangesToCompare.some(comparedRange => {
      if (!comparedRange.start || !comparedRange.end) return false
      return (
        startForValidation.isBetween(
          moment(normalizeYearlyRangeTime(comparedRange.start)),
          moment(normalizeYearlyRangeTime(comparedRange.end)),
          null,
          '[]',
        ) ||
        endForValidation.isBetween(
          moment(normalizeYearlyRangeTime(comparedRange.start)),
          moment(normalizeYearlyRangeTime(comparedRange.end)),
          null,
          '[]',
        ) ||
        (startForValidation.isSameOrBefore(comparedRange.start) &&
          endForValidation.isSameOrAfter(comparedRange.end)) ||
        (moment(normalizeYearlyRangeTime(comparedRange.start)).isBetween(
          startForValidation,
          endForValidation,
          null,
          '[]',
        ) ||
          moment(normalizeYearlyRangeTime(comparedRange.end)).isBetween(
            startForValidation,
            endForValidation,
            null,
            '[]',
          ))
      )
    })

    if (overlaping) {
      handleValidationError(
        `yearly-time-range-start-${range.id}`,
        'Overlapping with another time range',
      )
      handleValidationError(`yearly-time-range-end-${range.id}`, ' ')
      return (hasErrors = true)
    } else {
      handleValidationError(`yearly-time-range-start-${range.id}`, null)
      handleValidationError(`yearly-time-range-end-${range.id}`, null)
    }
  })

  return hasErrors
}

const validateTimeSlotsSchedule = (
  timeSlotsSchedule,
  handleValidationError,
) => {
  const timeSlotsNotSelected =
    timeSlotsSchedule[0] &&
    !timeSlotsSchedule[0].slots.morning &&
    !timeSlotsSchedule[0].slots.lunch &&
    !timeSlotsSchedule[0].slots.afternoon
  if (timeSlotsNotSelected) {
    handleValidationError('time-slots', 'Is required')
    return true
  } else {
    handleValidationError('time-slots', null)

    return false
  }
}

const validateDaysSelector = (daysData, handleValidationError) => {
  if (daysData.length == 0) {
    handleValidationError('days-selector', 'Is required')
    return true
  } else {
    handleValidationError('days-selector', null)
  }
}

const validateWeeklySchedule = (weeklyHoursSchedule, handleValidationError) => {
  if (weeklyHoursSchedule.length === 0) {
    return true
  }
  let haveErrors = false
  weeklyHoursSchedule[0].hours.forEach(hours => {
    const isInvalid =
      validateBreakTime(
        hours.id,
        hours.start,
        hours.end,
        'start',
        handleValidationError,
        weeklyHoursSchedule[0].hours,
        'weekly-hours',
      ) ||
      validateBreakTime(
        hours.id,
        hours.start,
        hours.end,
        'end',
        handleValidationError,
        weeklyHoursSchedule[0].hours,
        'weekly-hours',
      )
    if (isInvalid) {
      haveErrors = true
    }
  })
  return haveErrors
}

const validateDaylySchedule = (dailySchedule, handleValidationError) => {
  if (dailySchedule.length === 0) {
    return true
  }
  let haveErrors = false
  dailySchedule.forEach(daySchedule => {
    const haveSomeErrors = daySchedule.hours.some(range => {
      return (
        validateBreakTime(
          range.id,
          range.start,
          range.end,
          'start',
          handleValidationError,
          daySchedule.hours,
          `${daySchedule.day}-daily-hours`,
        ) ||
        validateBreakTime(
          range.id,
          range.start,
          range.end,
          'end',
          handleValidationError,
          daySchedule.hours,
          `${daySchedule.day}-daily-hours`,
        )
      )
    })
    if (haveSomeErrors) {
      haveErrors = true
    }
  })
  return haveErrors
}

const validateYearlySchedule = (yearlySchedule, handleValidationError) => {
  const overlaps = validateYearlyRangeTimesOverlaps(
    yearlySchedule,
    handleValidationError,
  )
  if (overlaps) {
    return true
  }
  const checks = yearlySchedule.map(timeRange => {
    return validateYearlyTimeRange(timeRange, handleValidationError)
  })

  const hasErrors = checks.some(check => check)
  return hasErrors
}

const validateEventSchedule = (eventSchedule, handleValidationError) => {
  let hasErrors = false
  if (!eventSchedule.startDate) {
    handleValidationError('startDate', 'Start date is required')
    hasErrors = true
  } else {
    handleValidationError('startDate', null)
  }
  if (!eventSchedule.endDate) {
    handleValidationError('endDate', 'End date is required')
    hasErrors = true
  } else {
    handleValidationError('endDate', null)
  }

  let hasTimeRangesErrors = false
  eventSchedule.breakTimes.forEach(breakTime => {
    const isInvalid =
      validateBreakTime(
        breakTime.id,
        breakTime.start,
        breakTime.end,
        'start',
        handleValidationError,
        eventSchedule.breakTimes,
        'breakTimes',
      ) ||
      validateBreakTime(
        breakTime.id,
        breakTime.start,
        breakTime.end,
        'end',
        handleValidationError,
        eventSchedule.breakTimes,
        'breakTimes',
      )
    if (isInvalid) {
      hasTimeRangesErrors = true
    }
  })
  return hasErrors || hasTimeRangesErrors
}

const to24Hour = time12h => {
  const [time, modifier] = time12h.split(' ')
  let [hours, minutes] = time.split(':').map(Number)

  if (modifier === 'AM' && hours === 12) {
    hours = 0
  } else if (modifier === 'PM' && hours !== 12) {
    hours += 12
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

const to12Hour = time24h => {
  let [hours, minutes] = time24h.split(':').map(Number)
  const modifier = hours >= 12 ? 'PM' : 'AM'

  hours = hours % 12 || 12 // convert 0 → 12 and 13–23 → 1–11

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )} ${modifier}`
}

const prepareDailySchedulePayload = schedule => {
  let data = []
  schedule.map(({ day, hours }) => {
    return hours.forEach(hour => {
      data.push({
        day,
        start: to24Hour(hour.start),
        end: to24Hour(hour.end),
      })
    })
  })
  return data
}

const convertScheduleResponseToDailySchedule = payload => {
  const scheduleMap = {}

  payload.forEach((item, index) => {
    const { day, start, end } = item

    if (!scheduleMap[day]) {
      scheduleMap[day] = []
    }

    scheduleMap[day].push({
      id: scheduleMap[day].length + 1,
      start: to12Hour(start),
      end: to12Hour(end),
    })
  })

  return Object.keys(scheduleMap).map(day => ({
    day,
    hours: scheduleMap[day],
  }))
}

const daysScheduleHaveErrors = (day, hours, validationErrors) => {
  const haveErrors = hours.some(hour => {
    return (
      validationErrors[`${day}-daily-hours-${hour.id}-start`] ||
      validationErrors[`${day}-daily-hours-${hour.id}-end`]
    )
  })
  return haveErrors
}

const removeDaysScheduleErrors = (daySchedule, handleValidationError) => {
  return daySchedule.hours.forEach(hour => {
    handleValidationError(
      `${daySchedule.day}-daily-hours-${hour.id}-start`,
      null,
    )
    handleValidationError(`${daySchedule.day}-daily-hours-${hour.id}-end`, null)
  })
}

const deepEqual = (a, b) => {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a !== typeof b) return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => deepEqual(item, b[index]))
  }

  if (typeof a === 'object') {
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every(key => deepEqual(a[key], b[key]))
  }

  return false
}

export {
  formatAddress,
  generateNextButtonLabel,
  generateBackButtonLabel,
  getDefaultCenter,
  timeToMinutes,
  validate12HourFormat,
  validateBreakTime,
  validateBreakTimesNumber,
  getDefaultHoursSchedule,
  getDefaultDaysSchedule,
  getDefaultTimeSlotsSchedule,
  getDefaultTimeSlotSchedule,
  convertDayValueToShortName,
  validateYearlyTimeRange,
  validateDaysSelector,
  validateTimeSlotsSchedule,
  validateYearlySchedule,
  validateEventSchedule,
  to24Hour,
  to12Hour,
  prepareDailySchedulePayload,
  convertScheduleResponseToDailySchedule,
  validateYearlyRangeStartTime,
  validateYearlyRangeEndTime,
  validateYearlyRangeTimesOverlaps,
  validateBreakTimeOverlap,
  validateWeeklySchedule,
  validateDaylySchedule,
  daysScheduleHaveErrors,
  removeDaysScheduleErrors,
  deepEqual,
}
